/**
 * Spring Animation Module — wraps popmotion for liquid-feel motion.
 *
 * Provides high-level helpers for common widget animations:
 * enter/exit, scale, slide, and interruptible springs.
 */

import { animate, spring as popmotionSpring } from 'popmotion'

// ---------------------------------------------------------------------------
// Spring presets — Apple-inspired defaults
// ---------------------------------------------------------------------------

export const SPRING_PRESETS = {
  /** Snappy entrance (cards, bubbles) */
  snappy: { stiffness: 300, damping: 28, mass: 0.8 },
  /** Gentle settle (avatar slide-in) */
  gentle: { stiffness: 170, damping: 26, mass: 1 },
  /** Bouncy overshoot (chat pill appear) */
  bouncy: { stiffness: 400, damping: 20, mass: 0.6 },
  /** Smooth fade (opacity transitions) */
  smooth: { stiffness: 200, damping: 30, mass: 1 },
} as const

export type SpringPreset = keyof typeof SPRING_PRESETS

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SpringAnimation {
  stop: () => void
}

export interface SpringOptions {
  from: number
  to: number
  preset?: SpringPreset
  stiffness?: number
  damping?: number
  mass?: number
  onUpdate: (value: number) => void
  onComplete?: () => void
}

// ---------------------------------------------------------------------------
// Core spring function
// ---------------------------------------------------------------------------

/** Run a spring animation. Returns a handle to stop it. */
export function springAnimate(opts: SpringOptions): SpringAnimation {
  const preset = opts.preset ? SPRING_PRESETS[opts.preset] : undefined

  const playback = animate({
    from: opts.from,
    to: opts.to,
    type: 'spring',
    stiffness: opts.stiffness ?? preset?.stiffness ?? 170,
    damping: opts.damping ?? preset?.damping ?? 26,
    mass: opts.mass ?? preset?.mass ?? 1,
    onUpdate: opts.onUpdate,
    onComplete: opts.onComplete,
  })

  return { stop: () => playback.stop() }
}

// ---------------------------------------------------------------------------
// High-level animation helpers for common patterns
// ---------------------------------------------------------------------------

/** Spring-animate a CSS transform property on an element. */
export function springTransform(
  el: HTMLElement,
  property: string,
  from: number,
  to: number,
  unit = '',
  preset: SpringPreset = 'snappy',
): SpringAnimation {
  return springAnimate({
    from,
    to,
    preset,
    onUpdate: (v) => {
      el.style.transform = `${property}(${v}${unit})`
    },
  })
}

/** Spring-animate opacity. */
export function springOpacity(
  el: HTMLElement,
  from: number,
  to: number,
  preset: SpringPreset = 'smooth',
  onComplete?: () => void,
): SpringAnimation {
  return springAnimate({
    from,
    to,
    preset,
    onUpdate: (v) => {
      el.style.opacity = String(v)
    },
    onComplete,
  })
}

/**
 * Combined enter animation: fade in + scale up + slide.
 * Returns a handle that stops all sub-animations.
 */
export function springEnter(
  el: HTMLElement,
  options: {
    from?: { opacity?: number; scale?: number; y?: number }
    preset?: SpringPreset
    onComplete?: () => void
  } = {},
): SpringAnimation {
  const { from = {}, preset = 'snappy', onComplete } = options
  const startOpacity = from.opacity ?? 0
  const startScale = from.scale ?? 0.92
  const startY = from.y ?? 8

  el.style.opacity = String(startOpacity)
  el.style.transform = `scale(${startScale}) translateY(${startY}px)`

  const animations: SpringAnimation[] = []
  let completed = 0
  const total = 3
  const checkDone = () => { if (++completed >= total) onComplete?.() }

  animations.push(springAnimate({
    from: startOpacity, to: 1, preset,
    onUpdate: (v) => { el.style.opacity = String(v) },
    onComplete: checkDone,
  }))

  animations.push(springAnimate({
    from: startScale, to: 1, preset,
    onUpdate: (v) => {
      const currentY = parseFloat(el.style.transform.match(/translateY\(([^)]+)\)/)?.[1] ?? '0')
      el.style.transform = `scale(${v}) translateY(${currentY}px)`
    },
    onComplete: checkDone,
  }))

  animations.push(springAnimate({
    from: startY, to: 0, preset,
    onUpdate: (v) => {
      const currentScale = parseFloat(el.style.transform.match(/scale\(([^)]+)\)/)?.[1] ?? '1')
      el.style.transform = `scale(${currentScale}) translateY(${v}px)`
    },
    onComplete: checkDone,
  }))

  return {
    stop: () => animations.forEach(a => a.stop()),
  }
}

/**
 * Combined exit animation: fade out + scale down + slide.
 * Removes the element from DOM on complete.
 */
export function springExit(
  el: HTMLElement,
  options: {
    to?: { opacity?: number; scale?: number; y?: number }
    preset?: SpringPreset
    remove?: boolean
    onComplete?: () => void
  } = {},
): SpringAnimation {
  const { to = {}, preset = 'smooth', remove = true, onComplete } = options
  const endOpacity = to.opacity ?? 0
  const endScale = to.scale ?? 0.92
  const endY = to.y ?? 8

  const animations: SpringAnimation[] = []
  let completed = 0
  const total = 3
  const checkDone = () => {
    if (++completed >= total) {
      if (remove) el.remove()
      onComplete?.()
    }
  }

  animations.push(springAnimate({
    from: parseFloat(el.style.opacity || '1'), to: endOpacity, preset,
    onUpdate: (v) => { el.style.opacity = String(v) },
    onComplete: checkDone,
  }))

  animations.push(springAnimate({
    from: 1, to: endScale, preset,
    onUpdate: (v) => {
      const currentY = parseFloat(el.style.transform.match(/translateY\(([^)]+)\)/)?.[1] ?? '0')
      el.style.transform = `scale(${v}) translateY(${currentY}px)`
    },
    onComplete: checkDone,
  }))

  animations.push(springAnimate({
    from: 0, to: endY, preset,
    onUpdate: (v) => {
      const currentScale = parseFloat(el.style.transform.match(/scale\(([^)]+)\)/)?.[1] ?? '1')
      el.style.transform = `scale(${currentScale}) translateY(${v}px)`
    },
    onComplete: checkDone,
  }))

  return {
    stop: () => animations.forEach(a => a.stop()),
  }
}
